import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../config/api";

type Platform = "ozon" | "wildberries" | "yandex";

type Store = {
  id: string;
  userId: string;
  platform: Platform;
  name: string;
  credentials: Record<string, any>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

const StoresList: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    } as Record<string, string>;
  }, []);

  const getPlatformBadgeClasses = (platform: Platform) => {
    if (platform === "ozon") return "bg-blue-100 text-blue-700 border-blue-200";
    if (platform === "wildberries")
      return "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200";
    return "bg-yellow-100 text-yellow-700 border-yellow-200"; // yandex
  };

  const fetchStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(getApiUrl("/api/stores"), {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Не удалось загрузить магазины");
      const data = await res.json();
      setStores(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Магазины</h1>
      </div>
      {error ? <div className="text-red-600">{error}</div> : null}
      {loading ? (
        <div>Загрузка...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stores.map((s) => (
            <button
              key={s.id}
              className="text-left rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition"
              onClick={() => navigate(`/stores/${s.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${getPlatformBadgeClasses(
                        s.platform
                      )}`}
                    >
                      {s.platform}
                    </span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full ${s.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                    >
                      {s.isActive ? "Активен" : "Отключен"}
                    </span>
                  </div>
                  <div className="text-base font-semibold text-gray-900">
                    {s.name}
                  </div>
                </div>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                Создан: {new Date(s.createdAt).toLocaleString()} • Обновлён:{" "}
                {new Date(s.updatedAt).toLocaleString()}
              </div>
            </button>
          ))}
          {stores.length === 0 ? (
            <div className="text-gray-600">Нет магазинов</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default StoresList;
